/**
 * « Comment ça marche » — page publique, version 2.
 *
 * Transposition React de la maquette validée
 * (« archer-comment-ca-marche-reference.html », instantané DOM de la page
 * rendue). Mise en page, styles inline et textes sont repris à
 * l'identique ; la logique du script de fin de page devient l'état et les
 * effets ci-dessous.
 *
 * La maquette découpe le contenu en CINQ sections aux fonds alternés
 * (#fff / #f4f4f1) plutôt qu'en une seule bande blanche coupée de filets :
 *   1. #journey — hero + carte d'exemple d'analyse (la seule animée)
 *   2. Trois façons de passer à l'action
 *   3. L'analyse complète
 *   4. Combien ça t'économise ?
 *   5. CTA final
 *
 * DÉROGATIONS, toutes voulues et toutes listées :
 *
 *   1. La nav et le footer de la maquette ne sont pas reproduits : la page
 *      monte le chrome public commun (PublicHeader + SiteFooterV2), donc
 *      le sélecteur de langue, le tiroir mobile et le footer unifié.
 *   2. Le ⚔️ du h1 est retiré : pas d'émoji dans les titres. Tous les
 *      autres émojis de la page (cartes de l'analyse complète) restent.
 *   3. Les CTA de la maquette n'ont aucune destination (ce sont des
 *      `<button>` nus) : ils sont câblés sur les vraies routes du site.
 *   4. « * Voir conditions générales » est un simple texte dans la
 *      maquette ; il devient cliquable vers /conditions-generales, avec
 *      exactement la même présentation — un renvoi qui ne mène nulle part
 *      n'est pas un renvoi.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import PageHead from '../components/seo/PageHead';
import { useSiteLanguage, readSiteCountry } from '../hooks/useSiteLanguage';
import { useHowItWorksT } from '../hooks/useHowItWorksT';
import useStartRoute from '../hooks/useStartRoute';
import { getPageMetadata } from '../lib/seo/metadata';
import './HowItWorksV2.css';
import '../styles/hero.css';

// Résolution tolérante de l'asset : si l'image n'est pas au build, le hero
// rend simplement sans elle plutôt que de casser la page.
const assetUrl = (m) => (m && m.default) || m || null;

// Mascotte du hero — le hibou aux commandes du pupitre. Décorative, statique.
let HERO_MASCOT_SRC = null;
try {
  // eslint-disable-next-line global-require
  HERO_MASCOT_SRC = assetUrl(require('../assets/mascotte-machine.png'));
} catch (_) {
  HERO_MASCOT_SRC = null;
}
// Dimensions INTRINSÈQUES du fichier, posées sur la balise pour que le
// navigateur réserve la boîte avant décodage (aucun layout shift). La
// largeur d'AFFICHAGE est en CSS — c'est elle qui varie au responsive.
const HERO_MASCOT_W = 760;
const HERO_MASCOT_H = 619;

// Entrée du funnel pour un visiteur public — la même que le CTA de la home.

const ROUTE_TEAM = '/attorneys';
const ROUTE_PLANS = '/plans';
const ROUTE_ENGINE = '/engine';
const ROUTE_TERMS = '/conditions-generales';

// Ce qui suit ne porte AUCUN texte : les libellés vivent dans
// i18n/howItWorks.json, ici ne restent que la présentation (émoji, teinte,
// couleur) et les CHIFFRES, qui ne se traduisent pas. Les listes sont
// appariées par INDICE avec leurs équivalents traduits — changer l'ordre
// ici sans le changer là-bas décalerait tout.

// Émojis des six cartes de l'analyse complète, dans l'ordre de `depth.cards`.
const DEPTH_EMOJIS = ['⚔️', '🛡️', '🎯', '⚠️', '📖', '📊'];

// Teinte des trois arguments de l'exemple, dans l'ordre de `example.args`.
const EXAMPLE_ARG_TONES = ['green', 'green', 'amber'];

// Couleur des trois chiffres clés, dans l'ordre de `example.kpis`.
const KPI_COLORS = ['#0a0a0f', '#15803d', '#b45309'];

/* ══════════════════════════════════════════════════════════════════════
   Animation de la carte d'exemple — valeurs du script de la référence
   ══════════════════════════════════════════════════════════════════════ */
// Circonférence du cercle de score (r = 42) et reliquat correspondant à
// 78/100. La maquette les écrit en dur ; on les garde tels quels plutôt
// que de les recalculer, pour que l'anneau finisse exactement où le sien
// finit.
const RING_FULL = 263.9;
const RING_END = 58.1;
const SCORE_TO = 78;

// Les trois chiffres clés : la valeur d'arrivée et le type d'unité. Le
// LIBELLÉ de l'unité est traduit (`example.units`), pas le nombre.
const KPI_ANIM = [
  { to: 379, unit: 'euro' },
  { to: 0, unit: 'euro' },
  { to: 12, unit: 'day' },
];

// Barre de projection : largeur figée (elle ne bouge pas), couleur, et
// pourcentage d'arrivée du compteur en dessous.
const PROJECTION = [
  { width: '45%', color: '#16a34a', to: 45 },
  { width: '30%', color: '#f59e0b', to: 30 },
  { width: '25%', color: '#e5e7eb', to: 25 },
];

const ANIM = {
  ringDur: 1200,
  kpiDur: 1000,
  kpiDelay: 200,
  kpiStep: 150,
  segDelay: 1600,
  segStep: 150,
  segDur: 1000,
};

const easeOut = (t) => 1 - (1 - t) ** 3;

function usePrefersReducedMotion() {
  // Lu DÈS le premier rendu, pas dans un effet : sinon la première passe
  // croit que le mouvement est autorisé, remet la carte à zéro, et c'est
  // seulement au rendu suivant qu'on découvre le contraire. Le visiteur
  // qui a demandé « moins d'animations » verrait la carte vide clignoter.
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    if (mq.addEventListener) { mq.addEventListener('change', apply); return () => mq.removeEventListener('change', apply); }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);
  return reduced;
}

/**
 * Anime la carte d'exemple à sa première entrée dans le champ, une seule
 * fois. Le mécanisme est celui de la maquette, au détail près :
 *
 *  · seuls `stroke-dashoffset`, `transform` et le TEXTE des compteurs
 *    changent — aucune propriété qui provoque un recalcul de mise en
 *    page, et `font-variant-numeric: tabular-nums` fige la largeur des
 *    chiffres pour que compter ne fasse pas danser la carte ;
 *  · l'écriture est impérative (refs), pas par `setState` : compter à
 *    60 images par seconde via l'état re-rendrait la page entière trente
 *    fois pour rien ;
 *  · le JSX rend les valeurs FINALES. Première image, absence de JS,
 *    `prefers-reduced-motion`, changement de langue : dans les quatre
 *    cas la carte est juste. L'animation ne fait que repartir de zéro
 *    par-dessus.
 *
 * La maquette collecte aussi `.hiw2-arg` et `.animLine` et définit une
 * fonction `reveal()` — mais ne les utilise JAMAIS. Les arguments et les
 * points de la lettre ne sont donc pas animés chez elle ; ils ne le sont
 * pas ici non plus.
 */
function useExampleAnimation(reduced, fmtKpi, fmtPct) {
  const cardRef = useRef(null);
  const ringRef = useRef(null);
  const scoreRef = useRef(null);
  const kpiRefs = useRef([]);
  const segRefs = useRef([]);
  const pctRefs = useRef([]);

  // Les formateurs suivent la langue ; l'animation, elle, ne se rejoue
  // pas quand elle change. On lit donc la version courante à chaque
  // image plutôt que de la capturer dans la fermeture.
  const fmt = useRef({ kpi: fmtKpi, pct: fmtPct });
  fmt.current = { kpi: fmtKpi, pct: fmtPct };

  useEffect(() => {
    const card = cardRef.current;
    const ring = ringRef.current;
    if (!card || !ring) return undefined;

    // Fin de course. Sert dans deux cas : `prefers-reduced-motion`, et le
    // basculement de cette préférence PENDANT l'animation — le nettoyage
    // coupe les minuteries, celle-ci pose l'état d'arrivée.
    const setFinal = () => {
      ring.setAttribute('stroke-dashoffset', String(RING_END));
      if (scoreRef.current) scoreRef.current.textContent = String(SCORE_TO);
      kpiRefs.current.forEach((el, i) => { if (el) el.textContent = fmt.current.kpi(i, KPI_ANIM[i].to); });
      segRefs.current.forEach((el) => { if (el) { el.style.transition = 'none'; el.style.transform = 'none'; } });
      pctRefs.current.forEach((el, i) => { if (el) el.textContent = fmt.current.pct(PROJECTION[i].to); });
    };

    if (reduced) { setFinal(); return undefined; }
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const timers = [];
    const frames = [];

    // État initial — la carte part à zéro.
    ring.setAttribute('stroke-dashoffset', String(RING_FULL));
    if (scoreRef.current) scoreRef.current.textContent = '0';
    kpiRefs.current.forEach((el, i) => { if (el) el.textContent = fmt.current.kpi(i, 0); });
    segRefs.current.forEach((el) => { if (el) el.style.transform = 'scaleX(0)'; });
    pctRefs.current.forEach((el) => { if (el) el.textContent = fmt.current.pct(0); });

    const count = (dur, delay, apply) => {
      timers.push(setTimeout(() => {
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / dur);
          apply(easeOut(p));
          if (p < 1) frames.push(requestAnimationFrame(tick));
        };
        tick(t0);
      }, delay));
    };

    const play = () => {
      count(ANIM.ringDur, 0, (p) => {
        ring.setAttribute('stroke-dashoffset', String(RING_FULL - (RING_FULL - RING_END) * p));
        if (scoreRef.current) scoreRef.current.textContent = String(Math.round(SCORE_TO * p));
      });
      KPI_ANIM.forEach((k, i) => {
        if (!kpiRefs.current[i]) return;
        count(ANIM.kpiDur, ANIM.kpiDelay + i * ANIM.kpiStep, (p) => {
          kpiRefs.current[i].textContent = fmt.current.kpi(i, Math.round(k.to * p));
        });
      });
      segRefs.current.forEach((el, i) => {
        if (!el) return;
        timers.push(setTimeout(() => {
          el.style.transition = 'transform 1s cubic-bezier(0.22, 1, 0.36, 1)';
          el.style.transform = 'scaleX(1)';
        }, ANIM.segDelay + i * ANIM.segStep));
      });
      PROJECTION.forEach((s, i) => {
        if (!pctRefs.current[i]) return;
        count(ANIM.segDur, ANIM.segDelay + i * ANIM.segStep, (p) => {
          pctRefs.current[i].textContent = fmt.current.pct(Math.round(s.to * p));
        });
      });
    };

    let played = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !played) { played = true; io.disconnect(); play(); }
      });
    }, { threshold: 0.3 });
    io.observe(card);

    return () => {
      io.disconnect();
      timers.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
    };
  }, [reduced]);

  return { cardRef, ringRef, scoreRef, kpiRefs, segRefs, pctRefs };
}

// ── Icônes ────────────────────────────────────────────────────────────
const IcoArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const IcoCheck = ({ size = 15, stroke = '#16a34a', width = 2.6, style }) => (
  <svg style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke}
       strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IcoPen = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" />
  </svg>
);
const IcoSend = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />
  </svg>
);
const IcoScale = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="M7 21h10" /><path d="M12 3v18" /><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
  </svg>
);
const IcoUpload = ({ size = 18 }) => (
  <svg style={{ flex: 'none' }} width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke="#1a56db" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

// ── Styles repris de la maquette ──────────────────────────────────────
const S = {
  section: { padding: '72px 40px' },
  wrap: { maxWidth: 1160, margin: '0 auto' },
  kicker: {
    fontSize: 11, fontWeight: 800, letterSpacing: '2.5px', textTransform: 'uppercase',
    color: '#1a56db', marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 'clamp(22px,2.4vw,28px)', fontWeight: 600, letterSpacing: '-.025em',
    color: '#0a0a0f', marginBottom: 8,
  },
  lead: { margin: 0, fontSize: 15, fontWeight: 400, lineHeight: 1.6, color: '#4b5563' },
  badgeExample: {
    fontSize: 10, fontWeight: 600, letterSpacing: '.06em', background: '#fffbeb',
    border: '1px solid #fde68a', color: '#b45309', borderRadius: 999, padding: '3px 10px',
  },
  metaLabel: {
    fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
    color: '#9ca3af', marginBottom: 6,
  },
  // Les compteurs : largeur de chiffre constante, sinon la carte tremble
  // pendant qu'ils montent.
  tabular: { fontVariantNumeric: 'tabular-nums' },
  // Le renvoi « * » sous les trois cartes, et le lien équipe juste après.
  footnote: {
    textAlign: 'center', marginTop: 16, fontStyle: 'italic', fontSize: 14,
    fontWeight: 400, color: '#9ca3af',
  },
};

export default function HowItWorksV2() {
  const navigate = useNavigate();
  // La langue vient du store partagé, pas d'un état local alimenté par le
  // callback du header : le corps de la page se re-rend maintenant en même
  // temps que la barre. `country` n'est lu qu'une fois — la Belgique est la
  // seule juridiction active, elle ne change pas sous les pieds du visiteur.
  const [language] = useSiteLanguage();
  const country = readSiteCountry();
  const t = useHowItWorksT(language);
  const reduced = usePrefersReducedMotion();

  // La clé du catalogue est `howItWorks`, pas `how-it-works` : la variante
  // en tirets ne correspondait à AUCUNE entrée, `getPageMetadata` rendait
  // null, et le `{metadata && …}` plus bas ne montait donc jamais PageHead.
  // La page n'avait ni titre, ni description, ni canonical — alors qu'elle
  // est déclarée au sitemap. Rien ne le signalait : un lookup manqué rend
  // null, et une page sans balises s'affiche exactement comme les autres.
  const metadata = getPageMetadata ? getPageMetadata('howItWorks', language) : null;
  // Une seule décision pour tout le site : cf. hooks/useStartRoute.js.
  const startRoute = useStartRoute();
  const goStart = () => navigate(startRoute);

  const kpis = t('example.kpis');
  const units = t('example.units');
  const exampleArgs = t('example.args');
  const letterPoints = t('example.letter');
  const depthCards = t('depth.cards');

  // Mise en forme des compteurs. La règle du singulier (« 0 jour »,
  // « 12 jours ») est celle de la maquette : pluriel au-delà de 1.
  const fmtKpi = useCallback((i, v) => (
    KPI_ANIM[i].unit === 'euro'
      ? `${v}${units.euro}`
      : `${v}${v > 1 ? units.dayMany : units.dayOne}`
  ), [units]);
  const fmtPct = useCallback((v) => `${v}%`, []);

  const anim = useExampleAnimation(reduced, fmtKpi, fmtPct);

  return (
    <div data-testid="hiw2-page" className="hiw2-page" lang={language} data-country={country}>
      {metadata && <PageHead metadata={metadata} />}
      <PublicHeader />

      <main style={{ maxWidth: '100vw', overflowX: 'hidden' }}>

        {/* ═══════════ 1 · HERO + EXEMPLE D'ANALYSE ═══════════ */}
        {/* `position: relative` + `overflow: hidden` : le contrat du halo
            commun (hero.css) — il déborde en haut et la section le rogne.
            Le conteneur de contenu passe en `relative` pour rester devant. */}
        <section id="journey" className="archer-hero-section" style={{ background: '#fff', paddingLeft: 40, paddingRight: 40, position: 'relative', overflow: 'hidden' }}>
          {/* Le fond doux commun ; `--wide` parce que le hero est en deux
              colonnes (texte + hibou au pupitre) : le halo enveloppe les deux. */}
          <div aria-hidden="true" className="archer-hero-halo archer-hero-halo--wide" />
          <div style={{ ...S.wrap, position: 'relative' }}>

            {/* Deux colonnes : la copy telle qu'elle était (toujours alignée
                à gauche), la mascotte à droite. Le `marginBottom` du bloc
                est porté par la RANGÉE et non par la copy — posé sur une
                colonne, il décalerait son centre par rapport à l'autre. */}
            <div className="hiw2-hero-row" style={{ marginBottom: 8 }}>
              <div className="hiw2-hero-copy">
                <div style={S.kicker}>{t('hero.kicker')}</div>
                {/* ⚔️ retiré du titre : pas d'émoji dans les titres. */}
                <h1 className="hiw2-h1 archer-hero-title">
                  {t('hero.h1a')}<br /><span className="archer-hero-accent">{t('hero.h1b')}</span>
                </h1>
                <p className="archer-hero-subtitle">{t('hero.sub')}</p>
                <button type="button" className="hiw2-cta" data-testid="hiw2-cta-hero" onClick={goStart}
                  style={{
                    marginTop: 22, display: 'inline-flex', alignItems: 'center', gap: 9,
                    padding: '13px 26px', border: 'none', background: '#1a56db', color: '#fff',
                    borderRadius: 12, fontSize: 15, fontWeight: 600, letterSpacing: '-.01em',
                    fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 4px 16px rgba(26,86,219,.2)',
                  }}>
                  {t('hero.cta')}<IcoArrow />
                </button>
              </div>
              {/* Purement décorative : aucune information qu'un lecteur
                  d'écran doive annoncer, aucun clic. Statique — la mascotte
                  ne bouge sur aucune page. */}
              {HERO_MASCOT_SRC && (
                <img
                  className="hiw2-hero-mascot"
                  data-testid="hiw2-hero-mascot"
                  src={HERO_MASCOT_SRC}
                  alt=""
                  aria-hidden="true"
                  width={HERO_MASCOT_W}
                  height={HERO_MASCOT_H}
                  loading="eager"
                  decoding="async"
                  draggable="false"
                />
              )}
            </div>

            <div style={{ marginTop: 56 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ ...S.kicker, marginBottom: 0 }}>{t('example.kicker')}</div>
                <span style={S.badgeExample}>{t('example.badge')}</span>
              </div>
              <div style={S.sectionTitle}>{t('example.title')}</div>
              <p style={{ ...S.lead, margin: '0 0 22px', maxWidth: 640 }}>{t('example.lead')}</p>

              <div ref={anim.cardRef} style={{ background: '#fff', border: '1px solid #ececea', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{
                  display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap',
                  padding: '26px 28px', borderBottom: '1px solid #f4f4f1',
                }}>
                  <div style={{ position: 'relative', flex: 'none', width: 96, height: 96 }}>
                    <svg width="96" height="96" viewBox="0 0 96 96" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
                      <circle cx="48" cy="48" r="42" fill="none" stroke="#ececea" strokeWidth="8" />
                      <circle ref={anim.ringRef} cx="48" cy="48" r="42" fill="none" stroke="#16a34a" strokeWidth="8"
                              strokeLinecap="round" strokeDasharray={String(RING_FULL)} strokeDashoffset={String(RING_END)} />
                    </svg>
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span ref={anim.scoreRef} style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.02em', color: '#0a0a0f', ...S.tabular }}>{SCORE_TO}</span>
                      <span style={{ fontSize: 11, fontWeight: 500, color: '#9ca3af' }}>/100</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
                      letterSpacing: '.04em', background: '#f0fdf4', border: '1px solid #dcfce7',
                      color: '#15803d', borderRadius: 999, padding: '4px 11px', marginBottom: 10,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a' }} />
                      {t('example.verdictBadge')}
                    </span>
                    <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.02em', color: '#15803d', marginBottom: 6 }}>
                      {t('example.verdictHeadline')}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 400, lineHeight: 1.6, color: '#4b5563', maxWidth: 520 }}>
                      {t('example.verdictSummary')}
                    </div>
                  </div>
                  {/* Le badge « EXEMPLE » n'est ni animé ni retardé : c'est
                      une mention obligatoire, elle est lisible dès la
                      première image. */}
                  <span style={{ ...S.badgeExample, flex: 'none', fontSize: 10.5, letterSpacing: '.05em', padding: '4px 11px' }}>
                    {t('example.badge')}
                  </span>
                </div>

                <div className="hiw2-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)' }}>
                  {kpis.map((k, i) => (
                    <div key={k.label} style={{
                      padding: '18px 24px',
                      borderRight: i < kpis.length - 1 ? '1px solid #f4f4f1' : 'none',
                    }}>
                      <div style={S.metaLabel}>{k.label}</div>
                      <div
                        ref={(el) => { anim.kpiRefs.current[i] = el; }}
                        style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.02em', color: KPI_COLORS[i], marginBottom: 2, ...S.tabular }}
                      >
                        {fmtKpi(i, KPI_ANIM[i].to)}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>{k.sub}</div>
                    </div>
                  ))}
                </div>

                <div style={{ padding: '22px 28px 6px', borderTop: '1px solid #f4f4f1' }}>
                  <div style={{ ...S.metaLabel, marginBottom: 14 }}>{t('example.argsLabel')}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                    {exampleArgs.map((a, i) => {
                      const tone = EXAMPLE_ARG_TONES[i];
                      // Numérotation « 01 », « 02 »… dérivée de l'indice : la
                      // maquette la portait en dur, mais c'est une position,
                      // pas un texte — elle n'a rien à faire dans le JSON.
                      const n = String(i + 1).padStart(2, '0');
                      return (
                        <div key={n} className="hiw2-arg" style={{
                          display: 'flex', alignItems: 'center', gap: 14, background: '#fafaf8',
                          borderLeft: `3px solid ${tone === 'green' ? '#16a34a' : '#f59e0b'}`,
                          borderRadius: 8, padding: '13px 16px',
                        }}>
                          <span style={{ flex: 'none', fontSize: 11, fontWeight: 700, color: '#9ca3af' }}>{n}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-.01em', color: '#0a0a0f' }}>
                              {a.title}
                            </div>
                            <div style={{ fontSize: 12.5, fontWeight: 400, color: '#6b7280', marginTop: 2 }}>
                              {t('example.legalBasis')} <span style={{ color: '#1a56db' }}>{a.src}</span>
                            </div>
                          </div>
                          <span style={{
                            flex: 'none', fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '3px 10px',
                            background: tone === 'green' ? '#f0fdf4' : '#fffbeb',
                            border: `1px solid ${tone === 'green' ? '#dcfce7' : '#fde68a'}`,
                            color: tone === 'green' ? '#15803d' : '#b45309',
                          }}>{a.tag}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="hiw2-split" style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24,
                  padding: '6px 28px 26px', borderTop: '1px solid #f4f4f1',
                }}>
                  <div style={{ paddingTop: 20 }}>
                    <div style={{ ...S.metaLabel, marginBottom: 14 }}>{t('example.letterLabel')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                      {letterPoints.map((p) => (
                        <div key={p} style={{
                          display: 'flex', gap: 9, alignItems: 'flex-start',
                          fontSize: 13.5, lineHeight: 1.5, color: '#374151',
                        }}>
                          <IcoCheck style={{ flex: 'none', marginTop: 2 }} /><span>{p}</span>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="hiw2-cta-flat" data-testid="hiw2-cta-pv" onClick={goStart}
                      style={{
                        width: '100%', padding: 13, background: '#1a56db', color: '#fff', border: 'none',
                        borderRadius: 11, fontFamily: 'inherit', fontSize: 14.5, fontWeight: 600,
                        letterSpacing: '-.01em', cursor: 'pointer',
                      }}>
                      {t('example.letterCta')}
                    </button>
                  </div>
                  <div style={{ paddingTop: 20 }}>
                    <div style={{ ...S.metaLabel, marginBottom: 14 }}>{t('example.projectionLabel')}</div>
                    <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', marginBottom: 12 }}>
                      {PROJECTION.map((s, i) => (
                        <div
                          key={s.width}
                          ref={(el) => { anim.segRefs.current[i] = el; }}
                          style={{ width: s.width, background: s.color, transformOrigin: 'left center' }}
                        />
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 16 }}>
                      {t('example.projection').map((label, i) => (
                        <div key={label}>
                          <div style={{ fontSize: 12.5, fontWeight: 500, color: '#4b5563' }}>{label}</div>
                          <div
                            ref={(el) => { anim.pctRefs.current[i] = el; }}
                            style={{ fontSize: 15, fontWeight: 700, color: '#0a0a0f', ...S.tabular }}
                          >
                            {fmtPct(PROJECTION[i].to)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.55, color: '#6b7280' }}>
                      {t('example.projectionNote')}
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                  padding: '14px 28px', background: '#fafaf8', borderTop: '1px solid #f4f4f1',
                  fontSize: 11.5, fontWeight: 500, letterSpacing: '.02em', color: '#9ca3af',
                  textTransform: 'uppercase',
                }}>
                  <span>{t('example.footLeft')}</span>
                  <span>{t('example.footRight')}</span>
                </div>
              </div>
              <div style={{
                textAlign: 'center', marginTop: 16, fontStyle: 'italic', fontSize: 14,
                fontWeight: 400, color: '#9ca3af',
              }}>
                {t('example.caption')}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 2 · TROIS FAÇONS D'AGIR ═══════════ */}
        <section style={{ background: '#f4f4f1', ...S.section }}>
          <div style={S.wrap}>
            <div style={{ textAlign: 'center', marginTop: 0, marginBottom: 24 }}>
              <div style={S.sectionTitle}>{t('ways.title')}</div>
              <p style={{ ...S.lead, margin: '0 auto', maxWidth: 480 }}>{t('ways.lead')}</p>
            </div>

            <div className="hiw2-ways" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
              <div style={{
                display: 'flex', flexDirection: 'column', background: '#fff',
                border: '1px solid #ececea', borderRadius: 16, padding: '24px 22px',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 11, background: '#f0fdf4', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: '#16a34a', marginBottom: 12,
                }}><IcoPen /></div>
                <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.015em', color: '#0a0a0f', marginBottom: 8 }}>
                  {t('ways.self.title')}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 400, lineHeight: 1.55, color: '#6b7280', marginBottom: 18 }}>
                  {t('ways.self.desc')}
                </div>
                <div style={{ marginTop: 'auto', borderTop: '1px solid #f4f4f1', paddingTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginBottom: 10 }}>
                    {t('ways.self.note')}
                  </div>
                  <button type="button" className="hiw2-cta-grey" data-testid="hiw2-cta-letter" onClick={goStart}
                    style={{
                      width: '100%', padding: 11, background: '#f0fdf4', color: '#15803d',
                      border: '1px solid #bbf7d0', borderRadius: 10, fontFamily: 'inherit',
                      fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>
                    {t('ways.self.cta')}
                  </button>
                </div>
              </div>

              <div style={{
                position: 'relative', display: 'flex', flexDirection: 'column', background: '#1a56db',
                border: '2px solid #1a56db', borderRadius: 16, padding: '23px 21px',
                boxShadow: '0 12px 32px rgba(26,86,219,.2)',
              }}>
                <span style={{
                  position: 'absolute', top: -11, left: 20, background: '#16a34a', color: '#fff',
                  fontSize: 10, fontWeight: 700, letterSpacing: '.08em', padding: '4px 11px', borderRadius: 999,
                }}>{t('ways.jurist.badge')}</span>
                <div style={{
                  width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,.16)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginBottom: 12,
                }}><IcoSend /></div>
                <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.015em', color: '#fff', marginBottom: 8 }}>
                  {t('ways.jurist.title')}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 400, lineHeight: 1.55, color: 'rgba(255,255,255,.85)', marginBottom: 18 }}>
                  {t('ways.jurist.desc')}
                </div>
                <div style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,.2)', paddingTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 10 }}>{t('ways.jurist.price')}</div>
                  <button type="button" className="hiw2-cta-white" data-testid="hiw2-cta-jurist" onClick={goStart}
                    style={{
                      width: '100%', padding: 11, background: '#fff', color: '#1a56db', border: 'none',
                      borderRadius: 10, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}>
                    {t('ways.jurist.cta')}
                  </button>
                </div>
              </div>

              <div style={{
                display: 'flex', flexDirection: 'column', background: '#fff',
                border: '1px solid #ececea', borderRadius: 16, padding: '24px 22px',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 11, background: '#fef3c7', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: '#b45309', marginBottom: 12,
                }}><IcoScale /></div>
                <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.015em', color: '#0a0a0f', marginBottom: 8 }}>
                  {t('ways.lawyer.title')}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 400, lineHeight: 1.55, color: '#6b7280', marginBottom: 18 }}>
                  {t('ways.lawyer.desc')}
                </div>
                <div style={{ marginTop: 'auto', borderTop: '1px solid #f4f4f1', paddingTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#1a56db', marginBottom: 10 }}>
                    {t('ways.lawyer.note')}
                  </div>
                  <button type="button" className="hiw2-cta-flat" data-testid="hiw2-cta-attorney"
                    onClick={() => navigate(ROUTE_TEAM)}
                    style={{
                      width: '100%', padding: 11, background: '#1a56db', color: '#fff', border: 'none',
                      borderRadius: 10, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>
                    {t('ways.lawyer.cta')}
                  </button>
                </div>
              </div>
            </div>

            {/* Le renvoi « * » des trois cartes. Texte simple dans la
                maquette — ici il ouvre les conditions générales, sans rien
                changer à sa présentation. */}
            <div style={S.footnote}>
              <button type="button" className="hiw2-textlink-muted" data-testid="hiw2-terms-link"
                onClick={() => navigate(ROUTE_TERMS)}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  font: 'inherit', color: 'inherit',
                }}>
                {t('ways.termsNote')}
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <button type="button" className="hiw2-textlink" data-testid="hiw2-team-link"
                onClick={() => navigate(ROUTE_TEAM)}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: '#1a56db',
                }}>
                {t('ways.teamLink')}
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════ 3 · L'ANALYSE COMPLÈTE ═══════════ */}
        <section style={{ background: '#fff', ...S.section }}>
          <div style={S.wrap}>
            <div style={{ marginTop: 0 }}>
              <div style={{ ...S.kicker, marginBottom: 12 }}>{t('depth.kicker')}</div>
              <div style={S.sectionTitle}>{t('depth.title')}</div>
              <p style={{ ...S.lead, margin: '0 0 22px', maxWidth: 620 }}>{t('depth.lead')}</p>
              <div className="hiw2-depth" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
                {depthCards.map((c, i) => (
                  <div key={c.title} style={{
                    display: 'flex', gap: 14, alignItems: 'flex-start', background: '#fff',
                    border: '1px solid #ececea', borderRadius: 14, padding: '18px 20px',
                  }}>
                    <div style={{ flex: 'none', fontSize: 24, lineHeight: 1, marginTop: 2 }}>{DEPTH_EMOJIS[i]}</div>
                    <div>
                      <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: '-.012em', color: '#0a0a0f', marginBottom: 3 }}>
                        {c.title}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.5, color: '#6b7280' }}>
                        {c.subtitle}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button type="button" className="hiw2-cta" data-testid="hiw2-cta-plans"
                  onClick={() => navigate(ROUTE_PLANS)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1a56db',
                    color: '#fff', fontSize: 14, fontWeight: 600, letterSpacing: '-.01em',
                    padding: '12px 24px', borderRadius: 12, border: 'none', fontFamily: 'inherit',
                    cursor: 'pointer', boxShadow: '0 4px 16px rgba(26,86,219,.2)',
                  }}>
                  {t('depth.cta')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 4 · COMBIEN ÇA T'ÉCONOMISE ═══════════ */}
        <section style={{ background: '#f4f4f1', ...S.section }}>
          <div style={S.wrap}>
            <div style={{ marginTop: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <div style={S.sectionTitle}>{t('savings.title')}</div>
                <span style={{ ...S.badgeExample, whiteSpace: 'nowrap' }}>{t('savings.badge')}</span>
              </div>
              <p style={{ ...S.lead, margin: '0 0 22px', maxWidth: 560 }}>{t('savings.lead')}</p>
              <div className="hiw2-compare" style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'stretch',
              }}>
                <div style={{
                  background: '#fafaf8', border: '1px solid #ececea', borderRadius: 16,
                  padding: '26px 28px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-.01em', color: '#6b7280', marginBottom: 10 }}>
                    {t('savings.tradTitle')}
                  </div>
                  <div style={{ fontSize: 'clamp(28px,3vw,36px)', fontWeight: 700, letterSpacing: '-.03em', color: '#b91c1c' }}>
                    {t('savings.tradValue')}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 400, color: '#9ca3af', marginTop: 8 }}>
                    {t('savings.tradNote')}
                  </div>
                </div>
                <div style={{
                  background: '#fff', border: '2px solid #1a56db', borderRadius: 16,
                  padding: '25px 27px', textAlign: 'center', boxShadow: '0 12px 32px rgba(26,86,219,.13)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-.01em', color: '#1a56db', marginBottom: 10 }}>
                    {t('savings.archerTitle')}
                  </div>
                  <div style={{ fontSize: 'clamp(28px,3vw,36px)', fontWeight: 700, letterSpacing: '-.03em', color: '#15803d' }}>
                    {t('savings.archerValue')}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 400, color: '#9ca3af', marginTop: 8 }}>
                    {t('savings.archerNote')}
                  </div>
                </div>
              </div>
              <div style={{
                marginTop: 18, textAlign: 'center', fontSize: 'clamp(16px,1.7vw,19px)',
                fontWeight: 600, letterSpacing: '-.02em', color: '#0a0a0f',
              }}>
                {t('savings.punch')}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 5 · CTA FINAL ═══════════ */}
        <section style={{ background: '#fff', ...S.section }}>
          <div style={S.wrap}>
            <div style={{ marginTop: 0, marginBottom: 8, textAlign: 'center' }}>
              <div style={{
                fontSize: 'clamp(24px,2.8vw,34px)', fontWeight: 600, letterSpacing: '-.03em',
                lineHeight: 1.12, color: '#0a0a0f',
              }}>
                {t('final.h1a')}<br /><span style={{ color: '#1a56db' }}>{t('final.h1b')}</span>
              </div>
              <div role="button" tabIndex={0} className="hiw2-drop" data-testid="hiw2-dropzone"
                onClick={goStart}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goStart(); } }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 12, marginTop: 24,
                  border: '1.5px dashed #bfdbfe', background: '#f8fbff', borderRadius: 12,
                  padding: '16px 30px', cursor: 'pointer',
                }}>
                <IcoUpload />
                <span style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: '-.01em', color: '#0a0a0f' }}>
                  {t('final.drop')}
                </span>
              </div>
              <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 400, color: '#9ca3af' }}>
                {t('final.micro')}
              </div>
              <div style={{ marginTop: 22 }}>
                <button type="button" className="hiw2-textlink-muted" data-testid="hiw2-engine-link"
                  onClick={() => navigate(ROUTE_ENGINE)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 500, color: '#6b7280',
                  }}>
                  {t('final.engineLead')}{' '}
                  <span style={{ color: '#1a56db', fontWeight: 600 }}>{t('final.engineLink')}</span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooterV2 />
    </div>
  );
}
